import { useMemo } from 'react';
import { EventPageVisibility } from '@graphql/generated-base';
import { useWebsitePagesByRole } from '@shared/hooks/useWebsitePagesByRole';
import { useWebsiteV2SitePrivacyQuery } from './useWebsitePrivacy.graphql.generated';
import { countedPages, toVisibility } from './useWebsitePrivacy.utils';
import type { WebsitePrivacy } from './useWebsitePrivacy.types';

/**
 * The event website's site-wide privacy, whichever website version the event is on: a V2 site's
 * own settings, which win, or, for a V1 event, a rollup of its pages (the hidden ones included) with
 * the event's password settings. Admin surfaces only: a V2 site reports its settings to the event's
 * admins alone.
 */
export const useWebsitePrivacy = (options: Readonly<{ eventHandle?: string; skip?: boolean }> = {}): WebsitePrivacy => {
  const { event, pages, websiteV2SiteId, isLoading: isLoadingPages } = useWebsitePagesByRole({ ...options, admin: true, includeHidden: true });
  const { data: siteData, loading: isSiteLoading } = useWebsiteV2SitePrivacyQuery({
    variables: { siteId: websiteV2SiteId ?? '' },
    batchMode: 'fast',
    skip: options.skip || !websiteV2SiteId,
  });
  const isLoading = isLoadingPages || (!!websiteV2SiteId && isSiteLoading && !siteData);
  return useMemo(() => {
    const counted = countedPages(pages);
    const privacy = websiteV2SiteId ? siteData?.carbonWebsiteSiteById?.settings?.privacy : undefined;
    return {
      visibility: toVisibility(privacy?.visibility, counted),
      hasPassword: privacy?.hasPassword ?? event?.info.hasEventPassword ?? false,
      validateGuestIdentity: privacy?.validateGuestIdentity ?? event?.settings.requiresGuestIdentification ?? false,
      protectedPageCount: counted.filter(page => page.visibility === EventPageVisibility.passwordProtected).length,
      pageCount: counted.length,
      isLoading,
    };
  }, [pages, websiteV2SiteId, siteData, event, isLoading]);
};
